/**
 * Champion ultimates — one per character (data/characters.js), charged by
 * UltimateSystem and fired with the ultimate key (KEYBINDS.ultimate).
 *
 * Each def is metadata plus up to three behavior hooks, all optional:
 *
 *   onStart(ctx)     once, the tick the ultimate is fired
 *   onTick(ctx, dt)  every sim tick while the window is open
 *   onEnd(ctx)       once, when the window closes
 *
 * `ctx` is `{ player, combat, eventBus, state, elapsed, duration }`. `state`
 * is a scratch object wiped at the start of every activation, so a def can
 * keep spiral angles / retrigger timers there without any per-run bookkeeping
 * of its own. Anything a hook switches ON in onStart it must switch OFF in
 * onEnd — UltimateSystem always runs onEnd, including when the run ends
 * mid-ultimate, so those flags can never leak past the window.
 *
 * Ultimates deal damage through CombatSystem (pooled projectiles or
 * applyAreaDamage), never by calling enemy.takeDamage directly, so every one
 * of them scales with the run's build exactly like a weapon does.
 */

// --- Requiem (Vess) ---------------------------------------------------------
// A rotating spiral of piercing spirit-lances. Spawn cadence and spin are
// tuned so the arms sweep just under a full revolution over the window —
// standing still covers everything around you, walking rakes the spiral
// across whatever you're facing.
const REQUIEM_ARMS = 3;
const REQUIEM_INTERVAL = 0.06; // seconds between volleys
const REQUIEM_SPIN = 2.6; // rad/sec
const REQUIEM_SPEED = 300;

// --- Bastion (Rook) ---------------------------------------------------------
const BASTION_SLAM_RADIUS = 78;
const BASTION_SLAM_DAMAGE = 90;
const BASTION_SLAM_KNOCKBACK = 52; // px, one-shot shove (minions only)
// Trimmed from 0.5 with the Awakening merge: Bastion was tuned as the whole
// defensive payload of an ultimate, and now stacks the surge's dodge bonus
// and damage on top of the plate refill.
const BASTION_DAMAGE_REDUCTION = 0.4;

// --- Zero Hour (Iris) -------------------------------------------------------
// The world crawls but Iris doesn't: everything hostile (bosses, minions,
// every bullet in flight) runs at this fraction of normal speed while her own
// movement, dash, and fire rate stay untouched.
const ZERO_HOUR_TIME_SCALE = 0.4;

// --- Thornsong (Thorn) ------------------------------------------------------
const THORNSONG_RADIUS_START = 34;
const THORNSONG_RADIUS_END = 92;
const THORNSONG_GROW_TIME = 1.2; // seconds to open to full radius
const THORNSONG_PULL = 78; // px/sec enemies are dragged inward
const THORNSONG_TICK = 0.22;
const THORNSONG_DAMAGE = 15; // per tick, per enemy inside

// --- Skybreaker (Kestrel) ---------------------------------------------------
const SKYBREAKER_DASH_DAMAGE = 2.4; // multiplier on the dash strike
const SKYBREAKER_DASH_RADIUS = 1.6;
const SKYBREAKER_LANDING_RADIUS = 46;
const SKYBREAKER_LANDING_DAMAGE = 44;

// --- Tutti (Aria) -----------------------------------------------------------
// Aria's whole fight is spent chasing a narrow on-beat window; Tutti simply
// gives it to her. Everything counts as on-beat for the duration (so Meter's
// damage bonus is continuous and the Measure gauge fills every beat), and the
// fire-rate boost is what turns that into "every weapon, every beat".
const TUTTI_FIRE_RATE = 1.5;
const TUTTI_REFRAIN_RADIUS = 2;

// --- Second Silence (Cairn) -------------------------------------------------
// The payoff button for Debt: the whole bank comes due at once instead of
// trickling back, and nothing can touch Cairn while it's being paid. Short on
// purpose — it's a clutch reset, not an offensive window.
const SECOND_SILENCE_SHOCKWAVE_RADIUS = 84;
const SECOND_SILENCE_SHOCKWAVE_DAMAGE = 70;
const SECOND_SILENCE_KNOCKBACK = 48;
// Floor on the heal, so firing it at full HP with an empty bank isn't a
// wasted button — a fraction of max HP, matching the clutch-heal shape
// Wellspring already uses.
const SECOND_SILENCE_MIN_HEAL_PCT = 0.15;

export const ULTIMATES = {
  requiem: {
    id: 'requiem',
    name: 'Requiem',
    color: '#7e97ff',
    duration: 2.2,
    description: 'A widening spiral of piercing lances sweeps the arena for 2.2s.',
    onStart({ state }) {
      state.angle = Math.random() * Math.PI * 2;
      state.timer = 0;
    },
    onTick({ state, player, combat }, dt) {
      state.angle += REQUIEM_SPIN * dt;
      state.timer -= dt;
      // `while`, not `if`: at a low frame rate one tick can span more than a
      // single volley interval, and a dropped volley would visibly punch a
      // gap in the spiral.
      while (state.timer <= 0) {
        state.timer += REQUIEM_INTERVAL;
        for (let i = 0; i < REQUIEM_ARMS; i++) {
          const angle = state.angle + (i * Math.PI * 2) / REQUIEM_ARMS;
          combat.pool.fire({
            x: player.x,
            y: player.y,
            vx: Math.cos(angle) * REQUIEM_SPEED,
            vy: Math.sin(angle) * REQUIEM_SPEED,
            radius: 3.5,
            damage: 24,
            hits: 6,
            hitCooldownDuration: 0.2,
            lifetime: 1.4,
            color: '#7e97ff',
          });
        }
      }
    },
  },

  bastion: {
    id: 'bastion',
    name: 'Bastion',
    color: '#7ad67a',
    duration: 8,
    description: 'Ground slam + shove, every plate restored, and 40% damage reduction for 8s.',
    onStart({ player, combat, eventBus }) {
      combat.applyAreaDamage({
        x: player.x,
        y: player.y,
        radius: BASTION_SLAM_RADIUS,
        damage: BASTION_SLAM_DAMAGE,
        player,
        knockback: BASTION_SLAM_KNOCKBACK,
      });
      player.restoreArmorPlates();
      player.ultimateDamageReduction = BASTION_DAMAGE_REDUCTION;
      eventBus.emit('ultimate:shockwave', {
        x: player.x, y: player.y, radius: BASTION_SLAM_RADIUS, color: '#7ad67a',
      });
    },
    onEnd({ player }) {
      player.ultimateDamageReduction = 0;
    },
  },

  zeroHour: {
    id: 'zeroHour',
    name: 'Zero Hour',
    color: '#e07ae0',
    duration: 5,
    description: 'For 5s the world crawls at 40% speed and every shot you land crits.',
    onStart({ player, combat }) {
      player.enemyTimeScale = ZERO_HOUR_TIME_SCALE;
      combat.guaranteedCrit = true;
    },
    onEnd({ player, combat }) {
      player.enemyTimeScale = 1;
      combat.guaranteedCrit = false;
    },
  },

  thornsong: {
    id: 'thornsong',
    name: 'Thornsong',
    color: '#e0955a',
    duration: 6.5,
    description: 'A briar vortex drags everything nearby into a grinder for 6.5s.',
    onStart({ state }) {
      state.tick = 0;
      state.radius = THORNSONG_RADIUS_START;
    },
    onTick({ state, player, combat, elapsed }, dt) {
      // Read by Game._drawUltimateField for the ring visual — the vortex is
      // the one ultimate whose hitbox is a place rather than a projectile,
      // so its exact reach has to be visible to be fair.
      state.radius = THORNSONG_RADIUS_START
        + (THORNSONG_RADIUS_END - THORNSONG_RADIUS_START) * Math.min(1, elapsed / THORNSONG_GROW_TIME);
      combat.pullEnemies({ x: player.x, y: player.y, radius: state.radius, distance: THORNSONG_PULL * dt });
      state.tick -= dt;
      if (state.tick <= 0) {
        state.tick += THORNSONG_TICK;
        combat.applyAreaDamage({
          x: player.x, y: player.y, radius: state.radius, damage: THORNSONG_DAMAGE, player,
        });
      }
    },
  },

  skybreaker: {
    id: 'skybreaker',
    name: 'Skybreaker',
    color: '#ffd166',
    duration: 6,
    description: 'For 6s dashes are free and unlimited, hit far harder, and detonate on landing.',
    onStart({ player, combat, eventBus, state }) {
      player.freeDash = true;
      combat.dashDamageMultiplier = SKYBREAKER_DASH_DAMAGE;
      combat.dashRadiusMultiplier = SKYBREAKER_DASH_RADIUS;
      // Detonate wherever each dash ends — the whole point of the window is
      // that movement itself becomes the weapon, so it can't be gated behind
      // actually connecting with something (that's the dash strike's job).
      state.unsubscribe = eventBus.on('player:dash-end', ({ x, y }) => {
        combat.applyAreaDamage({
          x, y, radius: SKYBREAKER_LANDING_RADIUS, damage: SKYBREAKER_LANDING_DAMAGE, player, knockback: 20,
        });
        eventBus.emit('ultimate:shockwave', { x, y, radius: SKYBREAKER_LANDING_RADIUS, color: '#ffd166' });
      });
    },
    onEnd({ player, combat, state }) {
      player.freeDash = false;
      combat.dashDamageMultiplier = 1;
      combat.dashRadiusMultiplier = 1;
      state.unsubscribe?.();
      state.unsubscribe = null;
    },
  },

  tutti: {
    id: 'tutti',
    name: 'Tutti',
    color: '#c9a0ff',
    duration: 5,
    description: 'For 5s the refrain carries with no falloff, twice as far, and everything fires half again as fast.',
    // Tutti is everyone playing at once, which is exactly what an unchecked
    // cascade is — the name survived the move off the beat (ACT2-BIBLE §6).
    // Swaps Aria's Refrain config for a boosted one rather than setting flags
    // CombatSystem would have to check: the mechanic reads player.refrainConfig
    // fresh on every kill, so replacing the object is the whole effect.
    onStart({ player, state }) {
      state.previousRefrain = player.refrainConfig;
      if (player.refrainConfig) {
        player.refrainConfig = {
          ...player.refrainConfig,
          radius: player.refrainConfig.radius * TUTTI_REFRAIN_RADIUS,
          chainFalloff: 1,
        };
      }
      // Stash and restore rather than divide back out: fireRateMultiplier can
      // legitimately change mid-window (a level-up pick), and dividing would
      // silently undo that pick along with the ultimate.
      state.previousFireRate = player.fireRateMultiplier ?? 1;
      player.fireRateMultiplier = state.previousFireRate * TUTTI_FIRE_RATE;
    },
    onEnd({ player, state }) {
      // `previousRefrain` is null for a non-Aria run that somehow held Tutti,
      // which is the correct value to put back either way.
      player.refrainConfig = state.previousRefrain ?? null;
      player.fireRateMultiplier = state.previousFireRate ?? 1;
    },
  },

  secondSilence: {
    id: 'secondSilence',
    name: 'Second Silence',
    color: '#8a9bb0',
    duration: 3,
    description: 'The whole debt comes due at once — a shockwave, the banked damage repaid, and 3s untouchable.',
    onStart({ player, combat, eventBus }) {
      combat.applyAreaDamage({
        x: player.x,
        y: player.y,
        radius: SECOND_SILENCE_SHOCKWAVE_RADIUS,
        damage: SECOND_SILENCE_SHOCKWAVE_DAMAGE,
        player,
        knockback: SECOND_SILENCE_KNOCKBACK,
      });
      const banked = player.debtBank ?? 0;
      const heal = Math.max(banked, player.maxHp * SECOND_SILENCE_MIN_HEAL_PCT);
      const before = player.hp;
      player.hp = Math.min(player.maxHp, player.hp + heal);
      player.debtBank = 0;
      if (player.hp > before) {
        eventBus.emit('player:healed', { amount: player.hp - before, source: 'secondSilence' });
      }
      // Total immunity for the window rather than an i-frame flag: isInvincible
      // is the dash's own, and _updateDash clears it when the burst ends.
      player.ultimateDamageReduction = 1;
      eventBus.emit('ultimate:shockwave', {
        x: player.x, y: player.y, radius: SECOND_SILENCE_SHOCKWAVE_RADIUS, color: '#8a9bb0',
      });
    },
    onEnd({ player }) {
      player.ultimateDamageReduction = 0;
    },
  },

  // --- Act III (ACT3-BIBLE.md §6) -------------------------------------------

  fairCopy: {
    id: 'fairCopy',
    name: 'Fair Copy',
    color: '#ffd166',
    duration: 6,
    description: 'For 6s glyphs are written instantly, never expire, and chain damage between each other.',
    onStart({ player }) {
      // One flag does all three things, read by Player._updateInscription (the
      // instant write and the frozen lifetimes) and CombatSystem._updateGlyphs
      // (the chain). Quill's whole ultimate is "the field, but finished".
      player.inscriptionInstant = true;
    },
    onEnd({ player }) {
      player.inscriptionInstant = false;
      // The lattice is not kept: every glyph standing when the window closes
      // resumes aging from a fresh lifetime, so Fair Copy is a burst rather
      // than a way to bank a permanent field.
      for (const glyph of player.glyphs ?? []) {
        glyph.life = Math.min(glyph.life, player.inscriptionConfig?.glyphLifetime ?? glyph.life);
      }
    },
  },

  stretto: {
    id: 'stretto',
    name: 'Stretto',
    color: '#66d9ff',
    duration: 5,
    description: 'For 5s the stance swap is free and both stances apply at once — fire rate and mitigation together.',
    onStart({ player }) {
      // In music, a stretto is entries piling in before the previous one has
      // finished. Here it is exactly that: both voices, at once, no waiting.
      player.bothStances = true;
    },
    onEnd({ player }) {
      player.bothStances = false;
    },
  },

  // --- The polarity champion (BULLET-HELL.md §D7) ---------------------------

  unison: {
    id: 'unison',
    name: 'Unison',
    // The resonance violet (POLARITY.color) — a release is what absorbing
    // EITHER tone buys, and for this window every bullet is either tone.
    color: '#d98cff',
    duration: 3.5,
    description: 'For 3.5s every toned bullet matches — the whole screen washes through you, and pays.',
    onStart({ player, eventBus }) {
      // One flag, read by SpawningSystem._checkPlayerCollisions. Deliberately
      // NOT immunity: absorbed bullets still fire `bullet:absorbed`, so the
      // window pays ultimate charge and resonance releases exactly as a read
      // tone does. Tine's ultimate is her signature turned all the way up, not
      // a different mechanic borrowed for three seconds.
      player.unison = true;
      eventBus.emit('ultimate:shockwave', {
        x: player.x, y: player.y, radius: 70, color: '#d98cff',
      });
    },
    onEnd({ player }) {
      player.unison = false;
    },
  },

};
